
import React, { useState, useEffect, useRef } from 'react';
import { PopupContent } from '@/types/popup-system';

interface CorruptedTransmissionPopupProps {
  content: PopupContent;
  onDismiss: () => void;
  onAction?: (action: string) => void;
}

// Glitch characters for corruption effect
const GLITCH_CHARS = ['█', '▓', '░', '▒', '?', '_', '/', '\\', '|', '#', '@', '!', '%', '&', '*'];

// Typewriter text component with glitch effect
const TypewriterText = ({ text, speed = 30 }: { text: string; speed?: number }) => {
  const [displayedText, setDisplayedText] = useState('');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isGlitching, setIsGlitching] = useState(false);
  
  useEffect(() => {
    if (currentIndex < text.length) {
      const timer = setTimeout(() => {
        // Occasionally glitch
        if (Math.random() < 0.08) {
          setIsGlitching(true);
          setDisplayedText(prev => prev + GLITCH_CHARS[Math.floor(Math.random() * GLITCH_CHARS.length)]);
          setTimeout(() => {
            setDisplayedText(prev => prev.slice(0, -1) + text[currentIndex]);
            setIsGlitching(false);
            setCurrentIndex(prev => prev + 1);
          }, 100);
        } else {
          setDisplayedText(prev => prev + text[currentIndex]);
          setCurrentIndex(prev => prev + 1);
        }
      }, speed);
      return () => clearTimeout(timer);
    }
  }, [currentIndex, text, speed]);
  
  return (
    <span className={isGlitching ? 'text-red-400' : ''}>
      {displayedText}
      {currentIndex < text.length && <span className="animate-pulse">▌</span>}
    </span>
  );
};

// Corrupted text line with random glitches
const CorruptedLine = ({ children, corruption = 0.1 }: { children: string; corruption?: number }) => {
  const [glitchedText, setGlitchedText] = useState(children);
  const originalRef = useRef(children);
  
  useEffect(() => {
    const interval = setInterval(() => {
      if (Math.random() < 0.3) {
        const chars = originalRef.current.split('');
        const corrupted = chars.map(c => 
          Math.random() < corruption ? GLITCH_CHARS[Math.floor(Math.random() * GLITCH_CHARS.length)] : c
        ).join('');
        setGlitchedText(corrupted);
        
        // Restore after brief moment
        setTimeout(() => setGlitchedText(originalRef.current), 150);
      }
    }, 2000);
    
    return () => clearInterval(interval);
  }, [corruption]);
  
  return <span>{glitchedText}</span>;
};

export const CorruptedTransmissionPopup = ({ content, onDismiss, onAction }: CorruptedTransmissionPopupProps) => {
  const [screenFlicker, setScreenFlicker] = useState(false);
  const [signalStrength, setSignalStrength] = useState(87);
  const [errorCodes, setErrorCodes] = useState<string[]>([]);
  
  // Generate transmission metadata
  const transmissionId = `TRM-${Date.now().toString(36).toUpperCase()}`;
  const frequency = `${(Math.random() * 1000 + 100).toFixed(2)} MHz`;
  
  useEffect(() => {
    // Screen flicker effect
    const flickerTimer = setInterval(() => {
      if (Math.random() < 0.15) {
        setScreenFlicker(true);
        setTimeout(() => setScreenFlicker(false), 100);
      }
    }, 500);
    
    // Signal strength fluctuation
    const signalTimer = setInterval(() => {
      setSignalStrength(prev => {
        const change = (Math.random() - 0.5) * 20;
        return Math.max(30, Math.min(100, prev + change));
      });
    }, 1000);
    
    // Random error codes
    const errorTimer = setInterval(() => {
      if (Math.random() < 0.2 && errorCodes.length < 3) {
        const codes = ['ERR_SYNC', 'PKT_LOSS', 'CRC_FAIL', 'TIMEOUT', 'OVERFLOW', 'NULL_PTR', 'SEGFAULT'];
        setErrorCodes(prev => [...prev, codes[Math.floor(Math.random() * codes.length)]]);
        setTimeout(() => setErrorCodes(prev => prev.slice(1)), 3000);
      }
    }, 2000);
    
    return () => {
      clearInterval(flickerTimer);
      clearInterval(signalTimer);
      clearInterval(errorTimer);
    };
  }, [errorCodes.length]);

  return (
    <div className={`fixed inset-0 z-[9999] flex items-center justify-center bg-black/95 transition-opacity ${
      screenFlicker ? 'opacity-70' : 'opacity-100'
    }`}>
      {/* CRT scanlines */}
      <div 
        className="fixed inset-0 pointer-events-none opacity-20"
        style={{
          background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,255,0,0.03) 2px, rgba(0,255,0,0.03) 4px)'
        }}
      />
      
      {/* Screen curve effect */}
      <div 
        className="fixed inset-0 pointer-events-none"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 0%, rgba(0,0,0,0.3) 100%)'
        }}
      />
      
      <div className="bg-black border border-green-500/50 max-w-lg w-full mx-4 relative font-mono shadow-lg shadow-green-500/20">
        {/* Glitch overlay */}
        {screenFlicker && (
          <div className="absolute inset-0 bg-green-500/10 z-50 pointer-events-none" />
        )}
        
        {/* Header bar */}
        <div className="bg-green-900/30 border-b border-green-500/30 px-3 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${signalStrength > 60 ? 'bg-green-500' : signalStrength > 30 ? 'bg-yellow-500' : 'bg-red-500'} animate-pulse`} />
            <span className="text-green-400 text-xs">
              SIGNAL: {signalStrength.toFixed(0)}%
            </span>
          </div>
          <span className="text-green-500/70 text-xs">{frequency}</span>
        </div>
        
        {/* Transmission info */}
        <div className="px-3 py-2 border-b border-green-500/20 text-xs text-green-500/70">
          <div className="flex justify-between">
            <span>[TRANSMISSION {transmissionId}]</span>
            <span>{new Date().toISOString()}</span>
          </div>
        </div>
        
        {/* Title */}
        <div className="px-4 py-2 border-b border-green-500/20">
          <h2 className="text-green-400 text-lg">
            <CorruptedLine corruption={0.05}>{content.title}</CorruptedLine>
          </h2>
        </div>
        
        {/* Message body */}
        <div className="p-4 min-h-[150px]">
          <div className="text-green-300 leading-relaxed whitespace-pre-wrap">
            <TypewriterText text={content.message} speed={20} />
          </div>
          
          {/* Error codes display */}
          {errorCodes.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {errorCodes.map((code, i) => (
                <span key={i} className="text-red-400 text-xs bg-red-900/30 px-2 py-0.5 border border-red-500/30">
                  [{code}]
                </span>
              ))}
            </div>
          )}
        </div>
        
        {/* Status bar */}
        <div className="px-3 py-2 border-t border-green-500/20 flex items-center justify-between">
          <div className="text-xs text-green-500/50">
            <span className="mr-4">RX: {Math.floor(Math.random() * 1000)}kb</span>
            <span>LATENCY: {Math.floor(Math.random() * 500)}ms</span>
          </div>
          
          <div className="flex gap-2">
            {content.actionButtons?.map((button, index) => (
              <button
                key={index}
                onClick={() => onAction?.(button.action)}
                className="bg-green-900/30 border border-green-500/50 text-green-400 px-3 py-1 text-xs hover:bg-green-800/30 transition-colors"
              >
                [{button.text}]
              </button>
            ))}
            {content.dismissible && (
              <button
                onClick={onDismiss}
                className="bg-red-900/30 border border-red-500/50 text-red-400 px-3 py-1 text-xs hover:bg-red-800/30 transition-colors"
              >
                [END TRANSMISSION]
              </button>
            )}
          </div>
        </div>
        
        {/* Bottom status */}
        <div className="bg-green-900/20 px-3 py-1 text-center text-xs text-green-500/40 border-t border-green-500/20">
          ═══ WEIRDOBEACH RELAY NETWORK ═══
        </div>
      </div>
    </div>
  );
};
